/**
 * Buyers for one VIN — the replacement for the retired `buyer_demand` JSON blob.
 *
 * One card, two sections:
 *   1. Likely buyers  — the cohort match engine's ranked recommendations
 *   2. Already engaged — buyers who bid / viewed / saved THIS VIN but aren't in (1)
 *
 * Both come from a single `GET /vehicles/{vin}/buyers` call, which runs the same
 * scoring the Broker board precomputes with, so a buyer ranks identically in every
 * view. The broker buyer-exclusion list is deliberately not applied here.
 */
import React, { useEffect, useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  BrokerBuyerMatch,
  BrokerBuyerEngagement,
  BrokerEngagedBuyer,
} from '../../api/types';
import './VinBuyersCard.css';

interface VinBuyersCardProps {
  vin: string;
  /** How many ranked buyers to request (the API caps at 25). */
  limit?: number;
}

const fmtMoney = (v: number | null | undefined): string =>
  v == null ? '–' : `$${Math.round(v).toLocaleString()}`;

const fmtInt = (v: number | null | undefined): string =>
  v == null ? '–' : Math.round(v).toLocaleString();

/** Retention is a ratio (paid / MMR); show it as % of MMR. */
const fmtRetention = (v: number | null | undefined): string =>
  v == null ? '–' : `${Math.round(v * 100)}% of MMR`;

const fmtDistance = (v: number | null | undefined): string =>
  v == null ? '–' : `${Math.round(v)} mi`;

const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '–';
  const d = new Date(v.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '–' : d.toLocaleDateString();
};

/**
 * Engagement badges, ordered by re-engagement value: a lost bidder is the single
 * most actionable signal, a view/save the weakest.
 */
const EngagementBadges: React.FC<{ engagement?: BrokerBuyerEngagement | null }> = ({
  engagement,
}) => {
  if (!engagement) return null;
  const badges: Array<{ key: string; label: string; tone: string }> = [];
  if (engagement.lost_bid) badges.push({ key: 'lost', label: 'Lost bid', tone: 'warn' });
  if (engagement.already_bidding) badges.push({ key: 'active', label: 'Bidding now', tone: 'hot' });
  if (engagement.won) badges.push({ key: 'won', label: 'Won', tone: 'good' });
  if (engagement.made_offer) badges.push({ key: 'offer', label: 'Made offer', tone: 'info' });
  if (!engagement.already_bidding && !engagement.lost_bid && engagement.has_bid) {
    badges.push({ key: 'bid', label: 'Bid', tone: 'info' });
  }
  if (engagement.workbook_saved) badges.push({ key: 'saved', label: 'Saved', tone: 'muted' });
  if (engagement.viewed_or_saved && !engagement.workbook_saved) {
    badges.push({ key: 'viewed', label: 'Viewed', tone: 'muted' });
  }
  if (!badges.length) return null;
  return (
    <span className="vbc-badges">
      {badges.map((b) => (
        <span key={b.key} className={`vbc-badge vbc-badge--${b.tone}`}>
          {b.label}
        </span>
      ))}
    </span>
  );
};

export const VinBuyersCard: React.FC<VinBuyersCardProps> = ({ vin, limit = 5 }) => {
  const [buyers, setBuyers] = useState<BrokerBuyerMatch[]>([]);
  const [engaged, setEngaged] = useState<BrokerEngagedBuyer[]>([]);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vin) return;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    siftApiClient
      .getVehicleBuyers(vin, limit, controller.signal)
      .then((res) => {
        setBuyers(res.buyers || []);
        setEngaged(res.other_engaged_buyers || []);
        setTotalCandidates(res.total_candidates || 0);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError('Could not load buyers for this VIN.');
        console.error('VinBuyersCard load failed', err);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [vin, limit]);

  if (isLoading) {
    return <div className="vbc-state">Loading buyers…</div>;
  }
  if (error) {
    return <div className="vbc-state vbc-state--error">{error}</div>;
  }
  if (!buyers.length && !engaged.length) {
    return <div className="vbc-state">No buyer activity found for this vehicle's cohort.</div>;
  }

  return (
    <div className="vbc">
      {buyers.length > 0 && (
        <section className="vbc-section">
          <h4 className="vbc-heading">
            Likely buyers
            {totalCandidates > buyers.length && (
              <span className="vbc-subtle"> · top {buyers.length} of {totalCandidates}</span>
            )}
          </h4>
          <div className="vbc-table-wrap">
            <table className="vbc-table">
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Match</th>
                  <th className="vbc-num">Distance</th>
                  <th className="vbc-num">Buys</th>
                  <th className="vbc-num">Avg paid</th>
                  <th className="vbc-num">Retention</th>
                  <th>Last bought</th>
                </tr>
              </thead>
              <tbody>
                {buyers.map((b) => (
                  <tr key={b.buyer_uid}>
                    <td>
                      <span className="vbc-buyer">{b.buyer_name || b.buyer_uid}</span>
                      {b.state && <span className="vbc-subtle"> · {b.state}</span>}
                      <EngagementBadges engagement={b.engagement} />
                    </td>
                    <td>{b.match_label}</td>
                    <td className="vbc-num">{fmtDistance(b.distance_miles)}</td>
                    <td className="vbc-num">{fmtInt(b.total_buys)}</td>
                    <td className="vbc-num">{fmtMoney(b.avg_sold_price)}</td>
                    <td className="vbc-num">{fmtRetention(b.avg_sold_adj_mmr_ret)}</td>
                    <td>{fmtDate(b.last_sold_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {engaged.length > 0 && (
        <section className="vbc-section">
          <h4 className="vbc-heading">
            Already engaged this VIN
            <span className="vbc-subtle"> · {engaged.length}</span>
          </h4>
          <div className="vbc-table-wrap">
            <table className="vbc-table">
              <thead>
                <tr>
                  <th>Buyer</th>
                  <th>Activity</th>
                  <th className="vbc-num">Bids</th>
                  <th className="vbc-num">VDP views</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {engaged.map((e) => (
                  <tr key={e.buyer_uid}>
                    <td>
                      <span className="vbc-buyer">{e.buyer_name || e.buyer_uid}</span>
                      {e.buyer_parent_name && (
                        <span className="vbc-subtle"> · {e.buyer_parent_name}</span>
                      )}
                    </td>
                    <td>
                      <EngagementBadges engagement={e.engagement} />
                    </td>
                    <td className="vbc-num">{fmtInt(e.engagement.bid_count)}</td>
                    <td className="vbc-num">{fmtInt(e.engagement.vdp_views)}</td>
                    <td>
                      {fmtDate(
                        e.engagement.last_bid_time || e.engagement.last_vdp_view_time,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default VinBuyersCard;
